import {ChangeDetectionStrategy, Component, computed, Inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {BaseEntity} from 'shared/models/BaseEntity';
import { ButtonGeneratorModuleModule } from "../button-generator/button-generator-module.module";
import { ButtonVariant } from '../button-generator/button-generator.component';
import { TranslocoModule } from '@jsverse/transloco';
import { UsersService } from 'services/api/users.service';
import { User } from 'shared/models/User';
import _, { Dictionary } from 'lodash';
import {TableColumn, TableColumnType} from 'shared/models/TableColumn';
import {
  TranslocoDatePipe,
  TranslocoCurrencyPipe,
  TranslocoPercentPipe,
  TranslocoDecimalPipe
} from '@jsverse/transloco-locale';

type AuditField = {
  label: string;
  value: string;
};

@Component({
  selector: 'app-about-record-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    ButtonGeneratorModuleModule,
    TranslocoModule,
    TranslocoDatePipe,
    TranslocoCurrencyPipe,
    TranslocoPercentPipe,
    TranslocoDecimalPipe
],
  templateUrl: './about-record-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
})
export class AboutRecordDialogComponent implements OnInit {
  readonly baseAuditFields: AuditField[] = [
    {label: 'COMMON.ID_LABEL', value: 'id' },
    {label: 'COMMON.CREATED_AT_LABEL', value: 'createdAt'},
    {label: 'COMMON.CREATED_BY_LABEL', value: 'createdBy'},
    {label: 'COMMON.LAST_UPDATED_AT_LABEL', value: 'lastUpdatedAt'},
    {label: 'COMMON.UPDATED_BY_LABEL', value: 'updatedBy'},
  ];

  protected readonly usersMap = signal<Dictionary<User> | undefined>(undefined);

  protected readonly tableFields = computed<AuditField[]>(() => {
    const tableColumns = this.data.tableColumns || [];
    const fields: AuditField[] = [];

    // excluding ACTIONS and SELECTABLE
    tableColumns.forEach(column => {
      if (column.type !== TableColumnType.ACTIONS && column.type !== TableColumnType.SELECTABLE) {
        fields.push({
          label: column.label,
          value: column.value as keyof BaseEntity,
        });
      }
    });

    return fields;
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: { entity: BaseEntity; entityLabel?: string; tableColumns?: TableColumn[] },
    readonly dialogRef: MatDialogRef<AboutRecordDialogComponent>,
    private readonly usersService: UsersService,
  ) {
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  private loadUsers(): void {
    this.usersService.getUserValues().subscribe({
      next: (response) => {
        this.usersMap.set(_.keyBy(response.data, 'id') as Dictionary<User>);
      },
      error: (error) => {
        console.error('Failed to load users:', error);
      }
    });
  }

  getFieldValue(fieldKey: string): any {
    return (this.data.entity as any)[fieldKey];
  }

  getUserDisplayName(userId: string | undefined): string {
    if (!userId) {
      return '—';
    }
    const user = this.usersMap()?.[userId];
    return user?.username ?? '';
  }

  getColumnForField(fieldKey: string): TableColumn | undefined {
    return this.data.tableColumns?.find(col => col.value === fieldKey);
  }

  getCurrencyCode(): string | undefined {
    return (this.data.entity as any)?.currency?.code;
  }

  protected readonly ButtonVariant = ButtonVariant;
  protected readonly TableColumnType = TableColumnType;
}

